/**
 * @module DemoModeCard
 * @purpose Platform admin card driving the Demo Mode enable/disable ceremony and the
 *   AI-provider route radio. Absent (not disabled) on deployments where Demo Mode is
 *   unsupported (spec FR13).
 * @depends contexts/demo-mode-context, DemoModeCeremonyModal, ProviderRadio
 * @dependedOnBy pages/PlatformSettingsPage
 *
 * @rules
 * - Performance: reads status from the shared DemoModeContext -- no own polling
 * - State: local ceremony-modal open/closed only
 * - Errors: delegated to DemoModeCeremonyModal / ProviderRadio
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDemoMode } from '../../contexts/demo-mode-context'
import { DemoModeCeremonyModal } from './DemoModeCeremonyModal'
import { ProviderRadio } from './ProviderRadio'

const CARD_STYLE: React.CSSProperties = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: '12px',
  padding: '24px',
}

const TITLE_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: '1.1rem',
  fontWeight: 600,
  color: 'var(--text)',
  marginBottom: '4px',
}

const DESCRIPTION_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.825rem',
  color: 'var(--text-dim)',
  marginBottom: '16px',
}

const STATUS_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.7rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--text-dim)',
  marginBottom: '16px',
}

const TOGGLE_BUTTON_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.7rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  padding: '8px 16px',
  borderRadius: '6px',
  border: 'none',
  cursor: 'pointer',
  background: 'var(--accent)',
  color: 'var(--btn-primary-text, #fff)',
}

const DEMO_ENVIRONMENTS = new Set(['dev', 'demo'])

/**
 * Demo Mode card for the platform admin settings page.
 *
 * @behaviour Returns null (absent, not disabled) unless `status.environment` is `dev` or
 *            `demo` (spec FR13). Opens `DemoModeCeremonyModal` targeting the opposite of
 *            the current `enabled` state. The provider radio's visibility is driven by
 *            `status.capabilities` (spec FR7 -- data-driven, not a hardcoded `enabled`
 *            check), so it stays absent even while `enabled` is true if the backend has
 *            not (yet) granted the `externalLlmSwitch` capability.
 * @errors n/a -- delegated to the ceremony modal and provider radio
 */
export function DemoModeCard(): React.ReactElement | null {
  const { t } = useTranslation('admin')
  const { status, refetch } = useDemoMode()
  const [modalOpen, setModalOpen] = useState(false)

  if (status?.environment === undefined || !DEMO_ENVIRONMENTS.has(status.environment)) {
    return null
  }

  const enabled = status.demoMode.enabled
  const showsProviderRadio = status.capabilities?.includes('externalLlmSwitch') ?? false

  return (
    <div style={CARD_STYLE}>
      <h3 style={TITLE_STYLE}>{t('demoMode.card.title')}</h3>
      <p style={DESCRIPTION_STYLE}>{t('demoMode.card.description')}</p>
      <p style={STATUS_STYLE}>
        {enabled ? t('demoMode.card.statusEnabled') : t('demoMode.card.statusDisabled')}
      </p>
      <button
        type="button"
        className="transition-opacity hover:opacity-90"
        style={TOGGLE_BUTTON_STYLE}
        onClick={() => {
          setModalOpen(true)
        }}
      >
        {enabled ? t('demoMode.card.disableButton') : t('demoMode.card.enableButton')}
      </button>
      {showsProviderRadio && (
        <div style={{ marginTop: '20px' }}>
          <ProviderRadio />
        </div>
      )}
      <DemoModeCeremonyModal
        open={modalOpen}
        targetEnabled={!enabled}
        onClose={() => {
          setModalOpen(false)
        }}
        onSuccess={() => {
          void refetch()
        }}
      />
    </div>
  )
}
